import type { Client } from '@src/clients/domain/Client';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { AdminEditOrderFooter } from '@src/orders/components/AdminEditOrderFooter';
import type { Order } from '@src/orders/domain/Order';
import { EditOrder } from '@src/orders/EditOrder';
import type { EditOrderPage } from '@src/orders/EditOrderProvider';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useUpdateOrder } from '@src/orders/hooks/useUpdateOrder';
import type { EditOrderFormInputs } from '@src/orders/schemas/EditOrderFormInputs';
import type { Tractor } from '@src/tractor/domain/Tractor';
import type { TrailerListing } from '@src/trailer/domain/TrailerList';
import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const AdminEditOrder: EditOrderPage = (props: {
  order: Order;
  client: Client;
  trailers: TrailerListing[];
  tractors: Tractor[];
  defaultValues: EditOrderFormInputs;
  onValuesChange: (values: EditOrderFormInputs) => void;
  disabledFields?: string[];
}) => {
  const { t } = useTranslation();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const { showToast } = useToast();
  const { refreshOrder } = useRefreshOrder({ id: props.order.id });
  const checkedFields = props.order.getFields();

  const { updateOrder } = useUpdateOrder({
    orderId: props.order.id,
    onSuccess: () => {
      refreshOrder();
      void navigateToOrderDetails({ orderId: props.order.id });
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('editOrder.error'),
      }),
  });

  const submitForm: SubmitHandler<EditOrderFormInputs> = (data) => {
    updateOrder({
      data,
      fields: checkedFields.fields,
    });
  };
  return (
    <EditOrder
      {...props}
      checkedFields={checkedFields}
      permitInstructions={props.order.getPermitsInstructions()}
      onSubmit={submitForm}
      ActionsFooter={AdminEditOrderFooter}
    />
  );
};
